import React, { useState, useEffect, useCallback } from "react";
import * as api from "./api";
import { GLOBAL_CSS } from "./globalCss";
import { C, F } from "./theme";
import { Icon } from "./icons";

// ─── Components ──────────────────────────────────────────────────────────────
import Landing from "./components/Landing";
import Login from "./components/Login";
import Order from "./components/Order";
import Confirm from "./components/Confirm";
import Track from "./components/Track";
import ClientSpace from "./components/ClientSpace";
import DeliveryChoice from "./components/DeliveryChoice";
import ReviewPage from "./components/ReviewPage";
import ColList from "./components/ColList";
import ColForm from "./components/ColForm";
import ColRecu from "./components/ColRecu";
import Manager from "./components/Manager";
import Admin from "./components/Admin";
import InstallBanner from "./components/InstallBanner";
import { BottomNav, Toast } from "./components/UI";

export default function App() {
  // ─── Persist view + data in sessionStorage ──────────────────────────────
  const [view, setViewRaw] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("delivery")) return "delivery";
    if (params.get("track")) return "track";
    if (params.get("review")) return "review";
    try { return sessionStorage.getItem("setal_view") || "landing"; } catch { return "landing"; }
  });
  const setView = useCallback((v) => {
    setViewRaw(v);
    try { sessionStorage.setItem("setal_view", v); } catch {}
    // Scroll to top on view change
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [user, setUser] = useState(() => api.getStoredUser());
  const [lastOrder, setLastOrder] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const trackId = params.get("track");
    if (trackId) return { id: trackId };
    try { const s = sessionStorage.getItem("setal_lastOrder"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [currentOrder, setCurrentOrder] = useState(null);
  // Toast state
  const [toast, setToast] = useState(null);

  // Persist lastOrder
  useEffect(() => {
    try { sessionStorage.setItem("setal_lastOrder", JSON.stringify(lastOrder)); } catch {}
  }, [lastOrder]);

  // Redirect logged-in users to their dashboard after refresh
  useEffect(() => {
    if (user && (view === "landing" || view === "login")) {
      const dashMap = { admin: "admin", manager: "mgr", collector: "col_list" };
      if (dashMap[user.role]) setView(dashMap[user.role]);
    }
  }, []); // eslint-disable-line

  // Listen for notification clicks from service worker
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === "NOTIFICATION_CLICK") {
        const { orderId, notifType } = event.data;
        if (orderId) {
          setLastOrder({ id: orderId });
          if (notifType === "order_ready" || notifType === "delivery_reminder") {
            setDeliveryOrderId(orderId);
            setView("delivery");
          } else {
            setView("track");
          }
        }
      }
    };
    navigator.serviceWorker?.addEventListener("message", handler);
    return () => navigator.serviceWorker?.removeEventListener("message", handler);
  }, [setView]); // eslint-disable-line

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Offline indicator
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => { setIsOffline(true); setToast({ message: "Vous êtes hors ligne", type: "warning" }); };
    const goOnline = () => {
      setIsOffline(false);
      navigator.serviceWorker?.controller?.postMessage({ type: "ONLINE" });
    };
    const swMsg = (e) => {
      if (e.data?.type === "OFFLINE_SYNC_DONE") {
        setToast({ message: `${e.data.count} action(s) synchronisée(s)`, type: "success" });
      }
    };
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    navigator.serviceWorker?.addEventListener("message", swMsg);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
      navigator.serviceWorker?.removeEventListener("message", swMsg);
    };
  }, []);

  const [deliveryOrderId, setDeliveryOrderId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("delivery") || null;
  });

  const [reviewOrderId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("review") || null;
  });

  // Show bottom nav only for client views
  const clientViews = ["landing", "order", "track", "client_space", "confirm"];
  const showBottomNav = clientViews.includes(view);

  const renderView = () => {
    // Vues protégées : rediriger vers login si pas d'utilisateur connecté
    const protectedViews = ["admin", "mgr", "col_list", "col_form", "col_recu"];
    if (protectedViews.includes(view) && !user) {
      return <Login setView={setView} setUser={setUser} />;
    }
    switch (view) {
      case "landing":      return <Landing setView={setView} />;
      case "login":        return <Login setView={setView} setUser={setUser} />;
      case "order":        return <Order setView={setView} setLastOrder={setLastOrder} />;
      case "confirm":      return <Confirm order={lastOrder} setView={setView} />;
      case "track":        return <Track setView={setView} initialId={lastOrder?.id} />;
      case "client_space": return <ClientSpace setView={setView} setLastOrder={setLastOrder} />;
      case "delivery":     return <DeliveryChoice orderId={deliveryOrderId} setView={setView} />;
      case "review":       return <ReviewPage orderId={reviewOrderId} setView={setView} />;
      case "col_list":     return <ColList setView={setView} setCurrentOrder={setCurrentOrder} user={user} />;
      case "col_form":     return <ColForm setView={setView} order={currentOrder} setCurrentOrder={setCurrentOrder} />;
      case "col_recu":     return <ColRecu setView={setView} order={currentOrder} />;
      case "mgr":          return <Manager setView={setView} user={user} />;
      case "admin":        return <Admin setView={setView} user={user} />;
      default:             return <Landing setView={setView} />;
    }
  };

  return <>
    {/* Toast notifications */}
    {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

    {/* Offline banner */}
    {isOffline && (
      <div role="status" style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 99998,
        background: C.warningSoft, color: C.warning,
        borderBottom: `1px solid ${C.warningBorder}`,
        padding: "8px 16px", fontSize: F.size.base, fontWeight: F.weight.medium,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
        animation: "slideDown .22s ease",
      }}>
        <Icon name="offline" size={14} />
        Hors ligne — synchronisation automatique au retour
      </div>
    )}

    {renderView()}

    {/* Bottom Navigation for mobile */}
    {showBottomNav && <BottomNav view={view} setView={setView} />}

    <InstallBanner />
  </>;
}
